import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    Chip,
    Button,
    Skeleton,
} from "@heroui/react";
import { LuArrowLeft, LuPencil, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import {
    PageHeader,
    ThemedText,
    ErrorState,
    EmptyState,
} from "@/components/ui";
import {
    formatDate,
    formatCurrency,
    formatPrice,
    formatThousands,
} from "@/utils";
import { useUser } from "@/context/UserContext";

// Nilai retur per baris dibulatkan ke rupiah (harga jual bisa 2 desimal, mis. 333,33)
const getLineAmount = (item) =>
    Math.round((Number(item.quantity) || 0) * (Number(item.price) || 0));

const SalesOrderReturnDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useUser();

    const { data, isLoading, error, refetch } = useFetchData(
        `sales-order-returns/${id}/show`,
    );

    if (error) {
        const is404 = error?.response?.status === 404;
        return (
            <div className="p-6">
                {is404 ? (
                    <EmptyState
                        title="Data Tidak Ditemukan"
                        description="Retur penjualan yang Anda cari tidak ditemukan atau sudah dihapus."
                        actionLabel="Kembali ke Daftar"
                        onAction={() => navigate("/sales/return")}
                    />
                ) : (
                    <ErrorState
                        title="Gagal Memuat Detail Retur Penjualan"
                        message={error?.message}
                        onRetry={refetch}
                    />
                )}
            </div>
        );
    }

    const returnData = data;
    const items = returnData?.items || [];
    const isPending = Number(returnData?.approval_status_id) === 1;

    // Calculate grand total from items
    const grandTotal = items.reduce(
        (sum, item) => sum + getLineAmount(item),
        0,
    );

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Detail Retur Penjualan"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Retur Penjualan", href: "/sales/return" },
                        isLoading
                            ? { label: "..." }
                            : { label: returnData?.return_number },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <Button
                        variant="flat"
                        size="sm"
                        startContent={<LuArrowLeft />}
                        onPress={() => navigate("/sales/return")}
                    >
                        Kembali
                    </Button>
                    <Button
                        variant="flat"
                        size="sm"
                        startContent={<LuPrinter />}
                        onPress={() =>
                            navigate(`/sales/return/${id}/print`)
                        }
                    >
                        Print
                    </Button>
                    {!isLoading && isPending && hasPermission("sales-order-return.edit") && (
                        <Button
                            as={Link}
                            to={`/sales/return/${id}/edit`}
                            color="primary"
                            size="sm"
                            startContent={<LuPencil />}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            </div>

            {/* Informasi Retur */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="px-4 pt-4 pb-0">
                    <ThemedText size="md" className="font-semibold">
                        Informasi Retur
                    </ThemedText>
                </CardHeader>
                <CardBody className="p-4">
                    {isLoading ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="flex flex-col gap-1">
                                    <Skeleton className="h-4 w-24 rounded-lg" />
                                    <Skeleton className="h-5 w-40 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <InfoItem
                                label="No. Retur"
                                value={returnData?.return_number}
                                bold
                            />
                            <InfoItem
                                label="Tanggal Retur"
                                value={formatDate(returnData?.return_date)}
                            />
                            <div className="flex flex-col gap-1">
                                <ThemedText
                                    size="xs"
                                    variant="secondary"
                                    className="font-medium"
                                >
                                    Status Approval
                                </ThemedText>
                                <div>
                                    <Chip
                                        color={
                                            returnData?.approval_status
                                                ?.badge_color || "default"
                                        }
                                        size="sm"
                                        variant="flat"
                                        className="font-medium"
                                    >
                                        {returnData?.approval_status?.name ||
                                            "Unknown"}
                                    </Chip>
                                </div>
                            </div>
                            <InfoItem
                                label="No. Sales Order"
                                value={
                                    returnData?.returnable?.order_number || "-"
                                }
                            />
                            <InfoItem
                                label="Pelanggan"
                                value={
                                    returnData?.returnable?.customer?.name ||
                                    "-"
                                }
                            />
                            <InfoItem
                                label="Nilai Retur"
                                value={formatCurrency(
                                    returnData?.return_amount,
                                )}
                                bold
                            />
                            {returnData?.approval_note && (
                                <div className="sm:col-span-2 lg:col-span-3">
                                    <InfoItem
                                        label="Catatan Approval"
                                        value={returnData.approval_note}
                                    />
                                </div>
                            )}
                            {returnData?.description && (
                                <div className="sm:col-span-2 lg:col-span-3">
                                    <InfoItem
                                        label="Keterangan"
                                        value={returnData.description}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Item Retur */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Item Retur
                    </ThemedText>
                    {!isLoading && (
                        <ThemedText size="xs" variant="caption">
                            {items.length} item
                        </ThemedText>
                    )}
                </CardHeader>
                <CardBody className="overflow-x-auto p-4 pt-2">
                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[...Array(3)].map((_, i) => (
                                <div
                                    key={i}
                                    className="flex w-full items-center gap-4"
                                >
                                    <Skeleton className="h-5 w-8 shrink-0 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 w-20 rounded-lg" />
                                    <Skeleton className="h-5 w-20 rounded-lg" />
                                    <Skeleton className="h-5 w-24 rounded-lg" />
                                    <Skeleton className="h-5 w-28 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : items.length === 0 ? (
                        <ThemedText
                            size="sm"
                            variant="secondary"
                            className="py-8 text-center"
                        >
                            Tidak ada item.
                        </ThemedText>
                    ) : (
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Barang</th>
                                    <th>Satuan</th>
                                    <th>Gudang</th>
                                    <th className="text-right">Jumlah</th>
                                    <th className="text-right">Harga</th>
                                    <th className="text-right">Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {index + 1}.
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {item.material?.name || "-"}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {item.unit?.name || "-"}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {item.warehouse?.name || "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatThousands(item.quantity)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText size="xs">
                                                {formatPrice(item.price)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText
                                                size="xs"
                                                className="font-medium"
                                            >
                                                {formatCurrency(
                                                    getLineAmount(item),
                                                )}
                                            </ThemedText>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="text-right font-semibold"
                                    >
                                        <ThemedText
                                            size="xs"
                                            className="font-semibold"
                                        >
                                            Total
                                        </ThemedText>
                                    </td>
                                    <td className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="font-semibold"
                                        >
                                            {formatCurrency(grandTotal)}
                                        </ThemedText>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </CardBody>
            </Card>

            {/* Footer */}
            {!isLoading && returnData && (
                <div className="flex flex-col gap-3 px-1">
                    {/* Metadata */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <ThemedText size="xs" variant="secondary">
                            Dibuat:{" "}
                            {formatDate(returnData.created_at, {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </ThemedText>
                        <ThemedText size="xs" variant="secondary">
                            ·
                        </ThemedText>
                        <ThemedText size="xs" variant="secondary">
                            Terakhir diubah:{" "}
                            {formatDate(returnData.updated_at, {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </ThemedText>
                    </div>

                    {/* Syarat & Ketentuan */}
                    <div className="space-y-1 leading-relaxed">
                        <ThemedText
                            size="xs"
                            variant="secondary"
                            className="font-medium"
                        >
                            Syarat & Ketentuan:
                        </ThemedText>
                        <ol className="text-default-400 marker:text-default-400 list-disc space-y-0.5 pl-4">
                            <li>
                                <ThemedText size="xs" variant="secondary">
                                    Retur hanya dapat dilakukan atas barang yang
                                    telah terbukti rusak atau tidak sesuai
                                    pesanan.
                                </ThemedText>
                            </li>
                            <li>
                                <ThemedText size="xs" variant="secondary">
                                    Pengajuan retur harus disertai bukti
                                    pendukung (foto/dokumen) dan diajukan
                                    maksimal 3x24 jam setelah barang diterima.
                                </ThemedText>
                            </li>
                            <li>
                                <ThemedText size="xs" variant="secondary">
                                    Retur yang telah disetujui akan mempengaruhi
                                    stok barang secara otomatis (Barang Masuk).
                                </ThemedText>
                            </li>
                            <li>
                                <ThemedText size="xs" variant="secondary">
                                    Nilai retur akan diperhitungkan dalam
                                    penyelesaian pembayaran pelanggan.
                                </ThemedText>
                            </li>
                        </ol>
                    </div>
                </div>
            )}
        </div>
    );
};

const InfoItem = ({ label, value, bold = false }) => (
    <div className="flex flex-col gap-1">
        <ThemedText size="xs" variant="secondary" className="font-medium">
            {label}
        </ThemedText>
        <ThemedText size="sm" className={bold ? "font-semibold" : ""}>
            {value}
        </ThemedText>
    </div>
);

export default SalesOrderReturnDetail;
